import { useMemo, useState } from 'react'
import { RESORTS } from '../data/resorts'
import type { Stamp, StampStatus } from '../lib/types'

/** Simplified continental NA outline in lat/lng-ish projected space (viewBox 0..800 x 0..420) */
const NA_OUTLINE =
  'M 118 72 L 148 58 L 190 52 L 230 48 L 268 55 L 300 68 L 328 88 L 348 112 L 362 138 L 378 155 L 402 162 L 430 158 L 458 148 L 490 142 L 522 148 L 548 162 L 568 182 L 582 205 L 590 228 L 598 248 L 612 262 L 638 270 L 662 278 L 688 292 L 708 312 L 718 338 L 710 358 L 688 368 L 662 362 L 640 348 L 618 332 L 598 318 L 572 308 L 548 302 L 522 308 L 498 322 L 478 338 L 458 348 L 432 352 L 408 348 L 388 338 L 368 322 L 348 308 L 322 298 L 298 292 L 272 288 L 248 282 L 228 268 L 212 248 L 198 228 L 182 208 L 162 192 L 138 178 L 118 162 L 102 142 L 95 118 L 98 92 Z'

type Props = {
  stamps: Stamp[]
  onStamp?: (resortId: string, status: StampStatus | null) => void
  interactive?: boolean
}

export function MapDots({ stamps, onStamp, interactive = true }: Props) {
  const byId = useMemo(() => new Map(stamps.map((s) => [s.resortId, s.status])), [stamps])
  const [hover, setHover] = useState<{ id: string; x: number; y: number } | null>(null)
  const [filter, setFilter] = useState<'all' | 'visited' | 'want'>('all')

  const minLng = -125
  const maxLng = -66
  const minLat = 31
  const maxLat = 52
  const w = 800
  const h = 420

  const project = (lat: number, lng: number) => {
    const x = ((lng - minLng) / (maxLng - minLng)) * w
    const y = ((maxLat - lat) / (maxLat - minLat)) * h
    return { x, y }
  }

  const hovered = hover ? RESORTS.find((r) => r.id === hover.id) : null
  const hoverStatus = hover ? byId.get(hover.id) : undefined

  const cycleStamp = (id: string) => {
    if (!onStamp) return
    const cur = byId.get(id)
    if (!cur) onStamp(id, 'visited')
    else if (cur === 'visited') onStamp(id, 'want')
    else onStamp(id, null)
  }

  const visible = RESORTS.filter((r) => {
    const st = byId.get(r.id)
    if (filter === 'visited') return st === 'visited'
    if (filter === 'want') return st === 'want'
    return true
  })

  const visitedCount = stamps.filter((s) => s.status === 'visited').length
  const wantCount = stamps.filter((s) => s.status === 'want').length

  return (
    <div className="map-panel">
      <div className="map-toolbar">
        <div className="map-legend">
          <span>
            <i className="lg-v" />
            visited · {visitedCount}
          </span>
          <span>
            <i className="lg-w" />
            wishlist · {wantCount}
          </span>
        </div>
        <div className="map-filters">
          {(['all', 'visited', 'want'] as const).map((f) => (
            <button
              key={f}
              type="button"
              className={`btn sm${filter === f ? ' primary' : ' ghost'}`}
              onClick={() => setFilter(f)}
            >
              {f === 'all' ? 'All' : f === 'visited' ? 'Skied' : 'Want'}
            </button>
          ))}
        </div>
      </div>

      <div className="map-canvas">
        <svg className="map-svg" viewBox={`0 0 ${w} ${h}`} role="img" aria-label="North America ski stamp map">
          <defs>
            <linearGradient id="mapSnow" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#f7fbff" />
              <stop offset="55%" stopColor="#eef5fb" />
              <stop offset="100%" stopColor="#e4eef8" />
            </linearGradient>
            <linearGradient id="landFill" x1="0" y1="0" x2="1" y2="1">
              <stop offset="0%" stopColor="#c5d8ea" />
              <stop offset="100%" stopColor="#a8c4de" />
            </linearGradient>
            <radialGradient id="glowV" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#1aa87a" stopOpacity="0.45" />
              <stop offset="100%" stopColor="#1aa87a" stopOpacity="0" />
            </radialGradient>
            <radialGradient id="glowW" cx="50%" cy="50%" r="50%">
              <stop offset="0%" stopColor="#d4a017" stopOpacity="0.4" />
              <stop offset="100%" stopColor="#d4a017" stopOpacity="0" />
            </radialGradient>
            <filter id="softGlow" x="-80%" y="-80%" width="260%" height="260%">
              <feGaussianBlur stdDeviation="2" result="b" />
              <feMerge>
                <feMergeNode in="b" />
                <feMergeNode in="SourceGraphic" />
              </feMerge>
            </filter>
          </defs>

          <rect width={w} height={h} fill="url(#mapSnow)" rx="16" />

          {/* soft grid */}
          {[0.15, 0.3, 0.45, 0.6, 0.75, 0.9].map((t) => (
            <g key={t} opacity="0.55">
              <line x1={0} x2={w} y1={h * t} y2={h * t} stroke="rgba(90,130,170,0.08)" />
              <line y1={0} y2={h} x1={w * t} x2={w * t} stroke="rgba(90,130,170,0.06)" />
            </g>
          ))}

          {/* land silhouette */}
          <path d={NA_OUTLINE} fill="url(#landFill)" stroke="rgba(70,110,150,0.55)" strokeWidth="2" />
          <path
            d={NA_OUTLINE}
            fill="none"
            stroke="rgba(255,255,255,0.55)"
            strokeWidth="4"
            opacity="0.5"
            transform="translate(0 2)"
          />

          {/* region labels */}
          <text x="210" y="195" className="map-label">West</text>
          <text x="380" y="230" className="map-label">Rockies</text>
          <text x="560" y="250" className="map-label">East</text>
          <text x="250" y="95" className="map-label">Canada</text>

          {visible.map((r) => {
            const { x, y } = project(r.lat, r.lng)
            if (x < 8 || x > w - 8 || y < 8 || y > h - 8) return null
            const status = byId.get(r.id)
            const isHover = hover?.id === r.id

            if (!status) {
              return (
                <g
                  key={r.id}
                  className={interactive ? 'map-dot-hit' : undefined}
                  onMouseEnter={() => setHover({ id: r.id, x, y })}
                  onMouseLeave={() => setHover(null)}
                  onClick={() => interactive && cycleStamp(r.id)}
                  style={{ cursor: interactive ? 'pointer' : 'default' }}
                >
                  <circle cx={x} cy={y} r={isHover ? 9 : 7} fill="transparent" />
                  <circle
                    className="dot"
                    cx={x}
                    cy={y}
                    r={isHover ? 3.8 : 2.8}
                    fill={isHover ? '#2a6a95' : 'rgba(70,110,150,0.55)'}
                  />
                </g>
              )
            }

            const visited = status === 'visited'
            return (
              <g
                key={r.id}
                filter="url(#softGlow)"
                className={interactive ? 'map-dot-hit' : undefined}
                onMouseEnter={() => setHover({ id: r.id, x, y })}
                onMouseLeave={() => setHover(null)}
                onClick={() => interactive && cycleStamp(r.id)}
                style={{ cursor: interactive ? 'pointer' : 'default' }}
              >
                <circle cx={x} cy={y} r={16} fill={visited ? 'url(#glowV)' : 'url(#glowW)'} />
                <circle
                  className={`dot ${visited ? 'visited visited-pulse' : 'want'}`}
                  cx={x}
                  cy={y}
                  r={visited ? 6 : 5.5}
                />
              </g>
            )
          })}
        </svg>

        {hover && hovered && (
          <div
            className="map-tooltip"
            style={{
              left: `${(hover.x / w) * 100}%`,
              top: `${(hover.y / h) * 100}%`,
            }}
          >
            <strong>{hovered.name}</strong>
            <span>
              {hovered.state}, {hovered.country}
              {hoverStatus === 'visited' ? ' · skied' : hoverStatus === 'want' ? ' · wishlist' : ''}
            </span>
            {interactive && (
              <em>Tap to {hoverStatus === 'visited' ? 'wishlist' : hoverStatus === 'want' ? 'clear' : 'stamp'}</em>
            )}
          </div>
        )}
      </div>

      {interactive && (
        <p className="map-hint faint">
          Tap a mountain to stamp it · tap again to wishlist · once more to clear
        </p>
      )}
    </div>
  )
}
